import { CheckOutlined, ClearOutlined, PlusOutlined } from '@ant-design/icons';
import { notification } from 'antd';
import React, { CSSProperties } from 'react';
import { graphicApi } from '../../../api';
import { getUniqueValuesArray, handleResponse } from '../../../Helper';
import { AlarmModel, AlarmState } from '../../../model/AlarmModel';
import { AlarmQueueTreeNode, PendingItem } from '../../../model/GraphicModel';
import { Acknowledge } from '../MenuBar/Icons';
import styles from './alarmQueue.module.scss';

export const alarmsIconStyle: CSSProperties = { color: 'white', width: 16 };
export const acknowledgeStyle: CSSProperties = { width: 10 };

export const getAllAlarmIds = async (mapIds: number[], isAcknowledge: boolean): Promise<AlarmModel[]> => {
	const response = await graphicApi.getAllAlarmQueueAlarmModels(mapIds, isAcknowledge);
	if (handleResponse(response)) {
		return [];
	}
	return response.Entity;
};

export const getAllAlarmModels = async (pendingIds: PendingItem[], isAcknowledge: boolean): Promise<AlarmModel[]> => {
	const response = await graphicApi.getAlarmQueueDeviceAlarmModels(pendingIds, isAcknowledge);
	if (handleResponse(response)) {
		return [];
	}
	return response.Entity;
};

export const getActualSelectedAlarmKeys = async (
	pendingIds: PendingItem[],
	checkedKeys: string[],
	isClearUnacknowledgedAllowed: boolean,
	isAcknowledge: boolean,
	isAddNote: boolean
): Promise<number[]> => {
	let pendingKeys: string[] = [];
	let showError = false;

	if (pendingIds.length > 0) {
		pendingKeys = await getAllAlarmKeysByPendingId(pendingIds, isAcknowledge);
	}

	const mergedKeys: string[] = getUniqueValuesArray(
		checkedKeys.filter(x => x.split('_').length > 1),
		pendingKeys
	);

	const actualSelectedAlarmKeys = mergedKeys.reduce<number[]>((result: number[], key: string) => {
		const alarmKeyArray: string[] = key.split('_');
		const alarmId = Number(alarmKeyArray[0]);
		const alarmState = alarmKeyArray[1];

		if (isAcknowledge) {
			if (alarmState === `${AlarmState.Active}`) {
				result.push(alarmId);
			}
		} else if (isAddNote || isClearUnacknowledgedAllowed || alarmState === `${AlarmState.Acknowledged}`) {
			result.push(alarmId);
		} else {
			showError = true;
		}

		return result;
	}, []);

	if (showError) {
		notification['error']({
			message: _('UnableToClearAlarmRule'),
		});
	}

	return actualSelectedAlarmKeys;
};

export const acknowledgeAllIcon = (
	<>
		<CheckOutlined className={styles.acknowledgeIcon} />
		<PlusOutlined className={styles.acknowledgePlusIcon} />
	</>
);

export const clearAllIcon = (
	<>
		<ClearOutlined className={styles.clearIcon} />
		<PlusOutlined className={styles.clearPlusIcon} />
	</>
);

const getAllAlarmKeysByPendingId = async (pendingIds: PendingItem[], isAcknowledge: boolean): Promise<string[]> => {
	const response = await graphicApi.getAlarmQueueAlarmKeys(pendingIds, isAcknowledge);
	if (handleResponse(response)) {
		return [];
	}

	return response.Entity;
};

export const acknowledgeIcon = (state: AlarmState) =>
	state === AlarmState.Acknowledged ? <Acknowledge elementStyle={acknowledgeStyle} title={_('Acknowledged')} /> : <></>;

export const isSameAlarmQueueKey = (alarmId: number, alarmQueueKey: string) =>
	`${alarmId}_${AlarmState.Active}` === alarmQueueKey || `${alarmId}_${AlarmState.Acknowledged}` === alarmQueueKey;

export const mergeArrays = <T extends {}>(arrayA: T[], arrayB: T[], predicate = (a: T, b: T) => a === b): T[] => {
	const clonedArray: T[] = [...arrayA];
	arrayB.forEach(bItem => (clonedArray.some(cItem => predicate(bItem, cItem)) ? null : clonedArray.push(bItem)));
	return clonedArray;
};

export const getAllPendingIds = (treeData: AlarmQueueTreeNode[]): PendingItem[] =>
	treeData?.filter(x => x.children === undefined).map<PendingItem>(x => ({ Address: x.Address, ObjectType: x.ObjectType }));
